import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { v2Tone } from '../theme/theme';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import SearchTrigger from './common/SearchTrigger';
import { formatDate, formatDateTime } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const DECLINED_URL = `${API_BASE}/api/recommended_purchase_orders/declined`;

// 🔴 Fuer reine DATUMSANGABEN, ohne erfundene Uhrzeit.
//
// `discontinued_at` ist ein `date` ohne Zeitanteil. Die frühere lokale Fassung
// drehte es durch `new Date(value).toLocaleString('de-DE', …)` und machte daraus
// „12. März 2027, 01:00" — eine Genauigkeit, die es nicht gibt, und in einer
// anderen Zeitzone waere es der Vortag. Seit PR 2 kommt die Form aus `format.js`
// (`formatDate` → `12.03.2027`, wie die Produktseite), und nur die beiden
// echten Zeitstempel (`decline_date`, `auto_undecline_date` — `ignored_at` ist
// ein `UtcDateTime`) gehen über `formatDateTime`.

//: 🔴 Sachliche Ausschluesse — das Produkt wird wegen einer EIGENSCHAFT nicht
//: empfohlen, nicht weil ein Bediener es abgelehnt hat.
//:
//: Der Unterschied traegt drei Zusicherungen dieser Liste:
//:   · Der **Undo-Knopf** ruft `/clear` → `ignored_at = NULL`. Bei einer
//:     sachlichen Zeile waere das wirkungslos, weil die Eigenschaft sie haelt
//:     — der Bediener hielte den Knopf fuer kaputt. (Derselbe Grund stand
//:     schon fuer `discontinued` im Code; `condition` kam am 2026-09-21 dazu
//:     und war zunaechst vergessen — Regel 1 §2b, der Verbraucher der
//:     Ableitung wurde nicht mitgefuehrt.)
//:   · **Kein Ablaufdatum.** Eine Ablehnung laeuft nach 14 Tagen ab, eine
//:     Eigenschaft nicht.
//:   · **Kein Ablehnungsdatum.**
//:
//: Als benannte Funktion, nicht als fuenf `===`-Vergleiche: die naechste
//: Ausschlussart kann so nicht wieder vergessen werden.
const istSachlicherAusschluss = (reason) =>
  reason === 'discontinued' || reason === 'condition';

const SACHLICHER_GRUND_LABEL = {
  discontinued: 'Discontinued',
  condition: 'Not new',
};

export default function DeclinedProductsDrawer({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('decline_date');
  // 🔴 Der Schubkasten zeigt bis zu 500 Zeilen ohne Zeitgrenze (zuletzt 88
  // gemessen). Ein Chip allein macht vier solche Zeilen darin nicht
  // auffindbar — Gate-14-Warnung W-5, von Gate 16 als B5 wiederholt.
  //
  // 🔴 Das Etikett nennt die OBERMENGE, nicht `discontinued`. Das Praedikat
  // ist seit dem 2026-09-21 `discontinued || condition`; ein Etikett, das
  // nur den Auslauf nennt, verspricht eine Teilmenge und liefert mehr.
  // Benannt nach dem, was die Zeile sichtbar macht („does not expire").
  const [nurSachlich, setNurSachlich] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(DECLINED_URL);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed loading declined products', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const sachlichAnzahl = useMemo(
    () => rows.filter((r) => istSachlicherAusschluss(r.reason)).length,
    [rows],
  );

  const filteredRows = useMemo(() => {
    const basis = nurSachlich ? rows.filter((r) => istSachlicherAusschluss(r.reason)) : rows;
    if (!search) return basis;
    const q = search.toLowerCase();
    return basis.filter((row) => {
      return (
        row.product_name.toLowerCase().includes(q) ||
        row.variant_name.toLowerCase().includes(q) ||
        row.sku.toLowerCase().includes(q) ||
        // `reason` gehoert in die Suche: wer „discontinued" tippt, meint genau
        // diese Zeilen, und ohne sie faende er nichts.
        String(row.reason || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, nurSachlich]);

  const handleSortRequest = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const valueFor = (row, field) => {
    if (field === 'product_name') {
      return `${row.product_name || ''} ${row.variant_name || ''}`.trim();
    }
    if (field.endsWith('_date')) {
      return row[field] || '';
    }
    return row[field] || '';
  };

  const comparator = (a, b) => {
    const valueA = valueFor(a, orderBy);
    const valueB = valueFor(b, orderBy);
    if (orderBy.includes('date')) {
      // 🔴 Ausgelaufene Zeilen haben KEIN `decline_date`. Mit `null → 0`
      // landeten sie beim Vorgabesortieren (`decline_date desc`) ans Ende von
      // bis zu 500 Zeilen — genau das Gegenteil von „auffindbar". Fehlt der
      // Wert der Sortierspalte, wird auf `discontinued_at` ausgewichen.
      const ersatz = (zeile, wert) => wert || zeile.discontinued_at || null;
      const rohA = ersatz(a, valueA);
      const rohB = ersatz(b, valueB);
      const timeA = rohA ? new Date(rohA).getTime() : 0;
      const timeB = rohB ? new Date(rohB).getTime() : 0;
      return order === 'asc' ? timeA - timeB : timeB - timeA;
    }
    const normalizedA = String(valueA).toLowerCase();
    const normalizedB = String(valueB).toLowerCase();
    if (normalizedA < normalizedB) return order === 'asc' ? -1 : 1;
    if (normalizedA > normalizedB) return order === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort(comparator);
    return copy;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- comparator derived from order/orderBy already in deps
  }, [filteredRows, order, orderBy]);

  useEffect(() => {
    if (!open) return undefined;
    reload();
    return undefined;
  }, [open, reload, refreshKey]);

  const handleClear = async (id) => {
    try {
      await axios.post(`${DECLINED_URL}/${id}/clear`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Failed clearing declined row', err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          mt: 8,
          height: 'calc(100% - 64px)',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
      }}
    >
      <Box sx={{ width: 840, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', p: 3, gap: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6">Declined recommendations</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Paper variant="outlined" sx={{ mb: 1, p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Stockout recommendations that are <strong>not</strong> being proposed
            right now — either declined by hand (auto-undecline after 14 days),
            discontinued by the manufacturer, or no longer new stock
            (<code>condition</code>). Neither of the last two expires.
          </Typography>
        </Paper>
        {/* 🔴 Der Schalter ist der eigentliche Auffindbarkeits-Mechanismus.
            Er erscheint nur, wenn es ueberhaupt etwas zu filtern gibt — ein
            dauerhaft sichtbarer Schalter fuer eine leere Menge ist Ballast. */}
        {sachlichAnzahl > 0 && (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                size="small"
                checked={nurSachlich}
                onChange={(e) => setNurSachlich(e.target.checked)}
              />
            }
            label={`Does not expire only (${sachlichAnzahl})`}
          />
        )}
        {/* 🔴 P34 (G15e): ein Suchfeld ist `SearchTrigger`, kein `TextField`
            mit Such-Platzhalter. `inputRole="searchbox"` gibt dem Feld die
            Rolle, an der P34 und die Dialogproben es finden; `label` ist der
            zugängliche Name (ohne Auslassungspunkte, Hauswert N7.5). */}
        <SearchTrigger
          value={search}
          onChange={setSearch}
          placeholder="Search declined products…"
          label="Search declined products"
          inputRole="searchbox"
        />

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
              <CircularProgress />
            </Stack>
          ) : sortedRows.length === 0 ? (
            <Typography color="text.secondary">Nothing declined yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'product_name' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'product_name'}
                      direction={orderBy === 'product_name' ? order : 'asc'}
                      onClick={() => handleSortRequest('product_name')}
                    >
                      Product / Variant
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'reorder_date' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'reorder_date'}
                      direction={orderBy === 'reorder_date' ? order : 'asc'}
                      onClick={() => handleSortRequest('reorder_date')}
                    >
                      Reorder date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'stockout_date' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'stockout_date'}
                      direction={orderBy === 'stockout_date' ? order : 'asc'}
                      onClick={() => handleSortRequest('stockout_date')}
                    >
                      Stockout
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'decline_date' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'decline_date'}
                      direction={orderBy === 'decline_date' ? order : 'asc'}
                      onClick={() => handleSortRequest('decline_date')}
                    >
                      Declined
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'auto_undecline_date' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'auto_undecline_date'}
                      direction={orderBy === 'auto_undecline_date' ? order : 'asc'}
                      onClick={() => handleSortRequest('auto_undecline_date')}
                    >
                      Auto undecline
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow key={`${row.id}-${row.sku}`}>
                    <TableCell>
                      <Typography fontWeight="bold">{row.product_name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.variant_name || '—'} · {row.sku}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(row.reorder_date)}</TableCell>
                    <TableCell>{formatDate(row.stockout_date)}</TableCell>
                    <TableCell>
                      {istSachlicherAusschluss(row.reason) ? (
                        <Stack spacing={0.25}>
                          <Chip
                            label={SACHLICHER_GRUND_LABEL[row.reason] || row.reason}
                            size="small"
                            variant="outlined"
                            sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, width: 'fit-content' })}
                          />
                          {/* Nur der Auslauf traegt ein Datum und eine Notiz.
                              `condition` hat beides nicht — die Eigenschaft
                              steht am Produkt, nicht an der Zeile. */}
                          {row.reason === 'discontinued' && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(row.discontinued_at)}
                              {row.discontinued_note ? ` · ${row.discontinued_note}` : ''}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        formatDateTime(row.decline_date)
                      )}
                    </TableCell>
                    <TableCell>
                      {/* 🔴 Bei einem Auslauf steht hier bewusst nichts. Eine
                          Ablehnung laeuft nach 14 Tagen ab, ein Auslauf nicht —
                          ein Datum waere hier die Zusage "kommt zurueck", und
                          die stimmt nicht. Das Backend liefert dafuer
                          `auto_undecline_date: null`. */}
                      {istSachlicherAusschluss(row.reason)
                        ? <Typography variant="body2" color="text.secondary">does not expire</Typography>
                        : formatDateTime(row.auto_undecline_date)}
                    </TableCell>
                    <TableCell align="right">
                      {/* 🔴 Der Undo-Knopf ruft `/clear`, und das setzt
                          `ignored_at = NULL`. Bei einem AUSGELAUFENEN Produkt
                          waere das wirkungslos: die Zeile bliebe draussen, weil
                          `discontinued_at` sie haelt — und der Bediener hielte
                          den Knopf fuer kaputt.

                          Ein Auslauf ist ausserdem eine kaufmaennische
                          Entscheidung. Sie gehoert auf die Produktseite, nicht
                          hinter einen Undo-Knopf in einer Liste. */}
                      {istSachlicherAusschluss(row.reason) ? (
                        <Tooltip title="A factual exclusion is lifted on the product page, not here.">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              component={RouterLink}
                              to={`/products/${row.sku}`}
                            >
                              Open product
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          startIcon={<UndoIcon />}
                          size="small"
                          variant="outlined"
                          onClick={() => handleClear(row.id)}
                        >
                          Clear
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
